import { Injectable, inject } from '@angular/core';
import { UiService } from './ui.service';
import { HttpService } from './http.service';
import { Observable, BehaviorSubject, Subject, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import dayjs from 'dayjs';
import { TranslateService } from '@ngx-translate/core';
import { api } from '../config/api.config';
import { SiteEntity } from '../models';
import { SocketService } from './socket.service';

const PREPARED = 50;

@Injectable({
    providedIn: 'root'
})
export class OrderService {
    private http = inject(HttpService);
    private socket = inject(SocketService);
    private uiService = inject(UiService);
    private translate = inject(TranslateService);

    private _orders = new BehaviorSubject<any[]>([]);
    public orders$ = this._orders.asObservable();

    private _orderEvents = new Subject<any>();
    public orderEvents$ = this._orderEvents.asObservable();

    /**
     * Socket subscriptions owned by initializeRealTimeUpdates.
     *
     * This method is called on app load, on every `currentSite$` emission, and
     * on every manual store switch. It used to subscribe without keeping the
     * Subscription, so each call stacked another raw handler on the same
     * socket: after N store switches a single `order:new` fired N+1 times, and
     * the merchant got N+1 toasts with the order prepended N+1 times to the
     * list. It only reset at logout, when removeAllListeners() ran.
     */
    private realtimeSubs: Subscription[] = [];

    initializeRealTimeUpdates(siteUniq: string) {
        // Drop the previous store's handlers before wiring the new ones.
        this.teardownRealTimeUpdates();

        // Generate a random device ID for this session if not exists
        let deviceId = localStorage.getItem('device_id');
        if (!deviceId) {
            deviceId = Math.random().toString(36).substring(7);
            localStorage.setItem('device_id', deviceId);
        }

        this.socket.connect({
            query: {
                suid: siteUniq,
                device: deviceId, // Identify this "device"
                api: false // Dashboard client
            }
        });

        this.realtimeSubs.push(
            this.socket.on('order:new').subscribe((newOrder: any) => {
                const currentOrders = this._orders.getValue();
                const orderWithTag = { ...newOrder, justUpdated: true };
                this._orders.next([orderWithTag, ...currentOrders]);
                this._orderEvents.next({ type: 'new', order: newOrder });

                this.uiService.info('orders.notifications.new_order', { ref: newOrder.ref || '' });
            }),
        );

        this.realtimeSubs.push(
            this.socket.on('order:getOrder').subscribe((update: any) => {
                const currentOrders = this._orders.getValue();
                const updatedOrders = currentOrders.map(order =>
                    (order.uniq === update.orderId || order.id === update.orderId)
                        ? { ...order, ...update, justUpdated: true }
                        : order
                );
                this._orders.next(updatedOrders);
                this._orderEvents.next({ type: 'update', update });

                this.uiService.success('orders.notifications.order_updated');
            }),
        );

        // 'order:cancelled' is only broadcast to the 'orders:admin' room on the
        // user socket server (port 8887) — never to the per-site pos socket, so
        // it can't be picked up by the `this.socket.on(...)` handlers above.
        // Join that room here too (in addition to DashboardNotificationsService)
        // so the open Orders list reflects a cancellation without a manual refresh.
        this.socket.connect({}, 'user');
        this.socket.emit('orders:admin:join', {}, 'user');

        this.realtimeSubs.push(
            this.socket.onConnect('user').subscribe(() => {
                this.socket.emit('orders:admin:join', {}, 'user');
            }),
        );

        this.realtimeSubs.push(
            this.socket.on<any>('order:cancelled', 'user').subscribe((data: any) => {
                const currentOrders = this._orders.getValue();
                const updatedOrders = currentOrders.map(order =>
                    (order.id === data.order_id || order.ref === data.order_ref)
                        ? {
                            ...order,
                            status: { code: 'CANCELLED', name: this.translate.instant('orders.status_names.cancelled') },
                            justUpdated: true,
                        }
                        : order
                );
                this._orders.next(updatedOrders);
                this._orderEvents.next({ type: 'cancelled', data });

                this.uiService.warning('orders.notifications.order_cancelled', { ref: data.order_ref || '' });
            }),
        );
    }

    /**
     * Removes the socket handlers registered by initializeRealTimeUpdates.
     *
     * Unsubscribing matters because SocketService.on() returns an Observable
     * whose teardown calls socket.off(handler) — so dropping the Subscription
     * is what actually detaches the handler from the socket, not just the RxJS
     * chain.
     */
    teardownRealTimeUpdates(): void {
        this.realtimeSubs.forEach((sub) => sub.unsubscribe());
        this.realtimeSubs = [];
    }

    setOrdersState(orders: any[]) {
        this._orders.next(orders);
    }


    getOrders(site: SiteEntity, pagination: any, filters: any = null): Observable<any> {
        // Capture pagination.min at call time to avoid race conditions
        // (pagination object is passed by reference and may be modified before response arrives)
        const isFirstPage = pagination.min === 0;
        
        let url = `/order/orders/${pagination.min}/${pagination.max}?suid=${site.uniq}`;

        if (filters) {
            if (typeof filters !== 'object' || filters === null) {
                url += `&delivery=${filters}`;
            } else {
                if (filters.delivery) url += `&delivery=${filters.delivery}`;
                if (filters.status && filters.status.length) url += `&status=${filters.status.join(',')}`;
                if (filters.start) url += `&start=${dayjs(filters.start).format('YYYY-MM-DD')}`;
                if (filters.end) url += `&end=${dayjs(filters.end).format('YYYY-MM-DD')}`;
                if (filters.shop) url += `&shop=${filters.shop}`;
                if (filters.paymentMethod) url += `&payment=${filters.paymentMethod}`;
            }
        }

        return this.http.get(url).pipe(
            tap((response: any) => {
                const list = Array.isArray(response) ? response : (response?.data || []);
                if (Array.isArray(list)) {
                    if (isFirstPage) {
                        this._orders.next(list);
                    } else {
                        const current = this._orders.getValue();
                        this._orders.next([...current, ...list]);
                    }
                }
            })
        );
    }

    /**
     * Print production orders
     */
    printProduction(site: SiteEntity | null = null, plans: any = null, comments: any = {}, clientUniq = false, options: any = null, user: any = null): Observable<any> {
        if (site) {
            const roleToUse = user && user._adminToken ? 'ADMIN' : 'CLIENT';
            return this.http.save(
                '/order/production',
                { plans, comments, clientUniq, options },
                '',
                site.uniq,
                '',
                roleToUse
            );
        }
        return new Observable(observer => observer.error('Site required'));
    }

    /**
     * Set document URL for printing
     */
    setDocUrl(user: any = null, isAdmin = false): string {
        let docUrl = '';
        if (isAdmin) {
            docUrl = api.url + '/order/item/print' + '?token=' + user._adminToken;
        } else {
            docUrl = api.url + '/order/item/print' + '?token=' + user._token;
        }
        return docUrl;
    }

    /**
     * Get planned orders
     */
    getPlannedOrders(site: SiteEntity): Observable<any> {
        return this.http.get(`/order/orders/planned?suid=${site.uniq}&admin=true`);
    }

    /**
     * Get all sites orders
     */
    getAllSitesOrders(filters: any = null): Observable<any> {
        let url = '/order/orders/listing';
        if (filters) {
            const queryParams: string[] = [];
            if (filters.status && filters.status.length) queryParams.push(`status=${filters.status.join(',')}`);
            if (filters.start) queryParams.push(`start=${dayjs(filters.start).format('YYYY-MM-DD')}`);
            if (filters.end) queryParams.push(`end=${dayjs(filters.end).format('YYYY-MM-DD')}`);
            if (filters.shop) queryParams.push(`suid=${filters.shop}`);
            if (filters.paymentMethod) queryParams.push(`payment=${filters.paymentMethod}`);
            if (filters.includeCancelled) queryParams.push('include_cancelled=1');

            if (queryParams.length) {
                url += '?' + queryParams.join('&');
            }
        }
        return this.http.get(url, 'ADMIN');
    }

    /**
     * Get site orders by date
     */
    siteOrders(uniq: string, date: string, filters: any = null): Observable<any> {
        let url = `/order/orders/listing/${uniq}/${date}`;
        if (filters) {
            const queryParams: string[] = [];
            if (filters.status && filters.status.length) queryParams.push(`status=${filters.status.join(',')}`);
            if (filters.paymentMethod) queryParams.push(`payment=${filters.paymentMethod}`);
            if (filters.includeCancelled) queryParams.push('include_cancelled=1');
            if (queryParams.length) {
                url += '?' + queryParams.join('&');
            }
        }
        return this.http.get(url, 'ADMIN');
    }

    getDayOrders(date: string, siteUniq: string, filters: any = null): Observable<any> {
        return this.siteOrders(siteUniq, date, filters);
    }

    /**
     * Get alert orders
     */
    getAlertOrders(): Observable<any> {
        return this.http.get('/order/orders/alert', 'ADMIN');
    }

    /**
     * Get order by reference (GraphQL)
     */
    getOrderByRef(ref: string): Observable<any> {
        const query = `query($ref: String, $order_by: String, $order_desc: Boolean) {
            orders (ref: $ref, order_by: $order_by, order_desc: $order_desc) {
                id,
                ref,
                uniq,
                status_id,
                user_id,
                method_id,
                site_id,
                collected_at,
                total,
                payed_at,
                receive_at,
                pushed_at,
                stripe_id,
                user {
                    username,
                    firstname,
                    lastname,
                    phone
                },
                order_status {
                    code,
                    name
                },
                order_method {
                    name,
                    color
                },
                order_item {
                    name,
                    quantity
                },
                site {
                    name
                }
            }
        }`;
        return this.http.gql_query(query, { ref: ref, order_by: 'id', order_desc: true }, 'ADMIN').pipe(
            // GraphQL returns `order_item` (snake_case from the schema) but
            // the templates everywhere else assume the REST-shaped `items`
            // field. Normalise here so the search-result table renders
            // line items instead of an empty Order column.
            map((res: any) => {
                if (res?.data?.orders) {
                    res.data.orders = res.data.orders.map((o: any) => ({
                        ...o,
                        items: o.order_item ?? o.items ?? [],
                    }));
                }
                return res;
            })
        );
    }

    /**
     * Cancel order (GraphQL mutation)
     */
    cancelOrder(id: number | string, code: string): Observable<any> {
        const query = `mutation($id: Int!, $code: String!) {
            cancel_order(id: $id, code: $code) {
                id,
                status_id
            }
        }`;
        return this.http.gql_query(query, { id: parseInt(id.toString()), code: code }, 'ADMIN');
    }

    /**
     * Prepare order (set status to PREPARED)
     */
    prepare(uniq: string, code: string, suid: string): Observable<any> {
        return this.http.save(
            '/order/order/status',
            { statusCode: PREPARED, seller_code: code },
            uniq,
            suid,
            "",
            'ADMIN'
        );
    }

    /**
     * Update order status
     */
    updateStatus(orderId: string, statusCode: number, siteUniq?: string): Observable<any> {
        return this.http.save(
            '/order/order/status',
            { statusCode },
            orderId,
            siteUniq
        );
    }

    /**
     * Get order details
     */
    getOrderDetails(orderUniq: string): Observable<any> {
        return this.http.get(`/order/${orderUniq}`);
    }

    /**
     * Update order items
     */
    updateOrderItems(orderUniq: string, items: any[]): Observable<any> {
        return this.http.save('/order/items', { items }, orderUniq);
    }

    /**
     * Calculate order total
     */
    calculateTotal(items: any[], discounts: any[] = []): number {
        let total = 0;
        items.forEach(item => {
            total += item.price * item.quantity;
        });
        discounts.forEach(discount => {
            total -= discount.amount;
        });
        return total;
    }

    /**
     * Apply voucher to order
     */
    applyVoucher(orderUniq: string, voucherCode: string): Observable<any> {
        return this.http.post('/order/voucher/apply', {
            orderUniq,
            voucherCode
        });
    }

    /**
     * Remove voucher from order
     */
    removeVoucher(orderUniq: string): Observable<any> {
        return this.http.post('/order/voucher/remove', {
            orderUniq
        });
    }

    /**
     * Get order history
     */
    getOrderHistory(orderUniq: string): Observable<any> {
        return this.http.get(`/order/${orderUniq}/history`);
    }

    /**
     * Print order
     */
    printOrder(orderUniq: string): Observable<any> {
        return this.http.post('/order/print', { orderUniq });
    }

    /**
     * Export orders
     */
    exportOrders(filters: any): Observable<any> {
        return this.http.post('/order/export', filters, 'ADMIN');
    }

    /**
     * Get order statistics
     */
    getOrderStats(siteUniq?: string, period = 'month'): Observable<any> {
        const params = siteUniq ? `?suid=${siteUniq}&period=${period}` : `?period=${period}`;
        return this.http.get(`/order/stats${params}`);
    }

    /**
     * Refund order
     */
    refundOrder(orderUniq: string, amount?: number, reason?: string): Observable<any> {
        return this.http.post('/order/refund', {
            orderUniq,
            amount,
            reason
        });
    }

    /**
     * Force complete order (admin)
     */
    forceComplete(orderUniq: string, code: string): Observable<any> {
        return this.http.save(
            '/order/force-complete',
            { code },
            orderUniq,
            '',
            '',
            'ADMIN'
        );
    }

    /**
     * Get orders by status
     */
    getOrdersByStatus(statusCode: number, siteUniq?: string): Observable<any> {
        const params = siteUniq ? `?status=${statusCode}&suid=${siteUniq}` : `?status=${statusCode}`;
        return this.http.get(`/order/orders${params}`);
    }

    /**
     * Get orders by date range
     */
    getOrdersByDateRange(startDate: string, endDate: string, siteUniq?: string): Observable<any> {
        const params = siteUniq
            ? `?start=${startDate}&end=${endDate}&suid=${siteUniq}`
            : `?start=${startDate}&end=${endDate}`;
        return this.http.get(`/order/orders/range${params}`);
    }

    /**
     * Send order notification
     */
    sendNotification(orderUniq: string, type = 'ready'): Observable<any> {
        return this.http.post('/order/notify', {
            orderUniq,
            type
        });
    }
}
